import type {
  AbridgedDeliveryStatus,
  AnnotationEnvelope,
  Briefing,
  BriefingEnvelope,
  DailyEditionEnvelope,
  FeedSource,
  FollowingEnvelope,
  InboxBrief,
  InboxBriefEnvelope,
  InboxBriefPreferences,
  ItemListEnvelope,
  PodcastEpisode,
  PodcastEpisodeEnvelope,
  PodcastPreferences,
  PodcastPreferencesEnvelope,
  ReaderItem,
  ReadingSessionInput,
  SaveItemEnvelope,
  SearchItemListEnvelope,
} from "./types";

const opening = `Attention is the quietest of our resources, and the most contested. We give it away in fragments, then wonder why nothing feels complete.

Deep attention is not about willpower or grim discipline. It is a gentle return to what is already here: the capacity to be present with one thing, in full, for as long as it deserves.

## The Shape of Quiet

Quiet is not empty. It is full of small, easily missed things: the weight of a thought before it forms; the pause between breaths; the faint shift of light on a wall.

> When we make room for quiet, we begin to notice the subtle and the true.

We often treat attention as a faucet that runs at full or empty. But it is more like a garden: it grows where we tend it. Over time, small, consistent acts of attention change what becomes possible.

| Practice | A small beginning |
| --- | --- |
| Reading | One page without switching |
| Listening | One answer before composing the next |

A note about patience[^1].

[^1]: Depth arrives by returning, not by rushing.`;

let items: ReaderItem[] = [
  demoItem("itm_attention", "The Case for Deep Attention", "Maya Chen", "Field Notes", 12, 0.35, opening),
  demoItem("itm_trees", "What Trees Remember", "Jonas Hildreth", "The Clearing", 9),
  demoItem("itm_benches", "A Small History of Public Benches", "Leila Morgan", "Common Ground", 10),
  demoItem("itm_violin", "The Weather Inside a Violin", "Adrian Burke", "Resonance", 11),
  demoItem("itm_enough", "Designing for Enough", "Sara Akter", "Made Thought", 8),
];

let following: FeedSource[] = [{
  id: "feed_demo_publication",
  title: "Field Notes",
  feed_url_host: "fieldnotes.example",
  feed_url_redacted: "https://fieldnotes.example/[redacted-feed-url]",
  feed_type: "atom",
  enabled: true,
  last_success_at: "2026-07-12T12:00:00.000Z",
}];

let inboxBriefEnabled = false;
let podcastPreferences: PodcastPreferences = { enabled: false, feed_active: false, updated_at: "" };
let podcastFeedUrl = "";
let inboxBrief: InboxBrief = {
  id: "ibf_demo",
  issue_date: "2026-07-12",
  title: "Inbox Brief · July 12",
  subtitle: "Three updates, without the backlog.",
  status: "inbox",
  covered_count: 3,
  created_at: "2026-07-12T10:00:00.000Z",
  archived_at: "",
  markdown: "# Inbox Brief · July 12\n\n## What happened\n\nThree routine updates arrived; their useful details are preserved below. [1] [2] [3]",
  sources: items.slice(0, 3).map((item) => ({ item_id: item.id, item_version: `demo:${item.id}`, title: item.title, site_name: item.site_name || "", takeaway: `The useful update from ${item.site_name}.`, disposition: "covered", disposition_at: "" })),
};

let podcastEpisode: PodcastEpisode = {
  id: "pce_demo",
  issue_date: "2026-07-12",
  title: "What your unread articles are saying",
  subtitle: "A spoken synthesis, with three originals worth your attention.",
  status: "inbox",
  created_at: "2026-07-12T11:00:00.000Z",
  variants: [
    { id: "pcv_demo_cedar", voice: "cedar", duration_seconds: 701 },
  ],
  sources: items.slice(0, 5).map((item, index) => ({
    item_id: item.id,
    item_version: `demo:${item.id}`,
    title: item.title,
    site_name: item.site_name || "",
    takeaway: index < 3 ? `The episode covers the central argument from ${item.site_name}.` : `This original from ${item.site_name} rewards reading in full.`,
    editorial_role: index < 3 ? "covered_here" : "read_original",
    original_status: index === 2 ? "queue" : "inbox",
    state: "pending",
    default_selected: index < 2,
  })),
};

let briefing: Briefing = {
  id: "pce_demo",
  issue_date: "2026-07-12",
  format_version: "abridged_v1",
  state: "ready",
  title: "Imprint Abridged — July 12, 2026",
  subtitle: "Five items from your Library, faithfully summarized.",
  entry_count: 5,
  covered_count: 5,
  read_full_count: 0,
  duration_seconds: 1440,
  audio_sha256: "demo",
  created_at: "2026-07-12T10:00:00.000Z",
  published_at: "2026-07-12T10:00:00.000Z",
  visual_markdown: "",
  actions: [],
  entries: items.map((item, index) => ({
    item_id: item.id,
    item_version: `demo:${item.id}`,
    original_status: "queue",
    deliberate_save: true,
    position: index + 1,
    treatment: "covered",
    title: item.title,
    site_name: item.site_name || "",
    author_name: item.author_name || "",
    orientation: `The author makes a specific case about ${item.title.toLowerCase()}.`,
    why_read: "",
    visual_markdown: `The author’s central point is preserved here as an article-specific executive summary. The argument, evidence, and qualifications stay attached to this source rather than being blended into a theme across the rest of the edition.`,
    confidence: 0.96,
    state: index < 3 ? "archived" : "pending",
    state_updated_at: "2026-07-12T10:00:00.000Z",
  })),
};

function demoItem(
  id: string,
  title: string,
  byline: string,
  site: string,
  minutes: number,
  progress = 0,
  markdown = `# ${title}\n\nA quiet reading specimen for the Imprint web reader.`,
): ReaderItem {
  return {
    id,
    title,
    url: `https://example.com/${id}`,
    byline,
    author_name: byline,
    site_name: site,
    estimated_minutes: minutes,
    reading_progress: progress,
    status: "queue",
    reader_status: "readable",
    content_type: "article",
    markdown,
    tags: [{ id: "tag_essay", name: "Essay" }],
  };
}

function envelope(value: ReaderItem[]): ItemListEnvelope {
  return { ok: true, items: value.map((item) => ({ ...item, markdown: undefined })), has_more: false, next_cursor: null };
}

export const demoApi = {
  async currentBriefing(): Promise<Briefing> { return { ...briefing }; },
  async abridgedDelivery(): Promise<AbridgedDeliveryStatus> {
    return {
      issue_date: briefing.issue_date,
      state: "ready",
      error_code: "",
      attempts: 1,
      next_recovery_at: "",
      timezone: "America/New_York",
      last_sweep_kind: "scheduled",
      last_sweep_decision: "requested",
      last_sweep_at: briefing.published_at,
    };
  },
  async briefings(): Promise<BriefingEnvelope> {
    const { entries: _entries, actions: _actions, visual_markdown: _visual, ...summary } = briefing;
    return { ok: true, briefings: [summary], has_more: false, next_cursor: null };
  },
  async briefing(id: string): Promise<Briefing> {
    if (id !== briefing.id) throw new Error("briefing_not_found");
    return { ...briefing };
  },
  async restoreBriefingEntry(_briefingId: string, itemId: string): Promise<Briefing> {
    briefing = { ...briefing, entries: briefing.entries.map((entry) => entry.item_id === itemId ? { ...entry, state: "restored" } : entry) };
    return { ...briefing };
  },
  async listItems(status: string, _order: string, _cursor = "", favorite?: boolean): Promise<ItemListEnvelope> {
    const selected = items.filter((item) => {
      const statusMatches = status === "all" || (status === "active" ? item.status !== "archive" : item.status === status);
      return statusMatches && (favorite === undefined || Boolean(item.is_favorite) === favorite);
    });
    return envelope(selected);
  },
  async itemSummaries(ids: string[], status = "queue,inbox"): Promise<ItemListEnvelope> {
    const allowedStatuses = new Set(status.split(","));
    const byId = new Map(items.filter((item) => allowedStatuses.has(item.status)).map((item) => [item.id, item]));
    return envelope(ids.map((id) => byId.get(id)).filter((item): item is ReaderItem => Boolean(item)));
  },
  async search(query: string): Promise<SearchItemListEnvelope> {
    const value = query.toLowerCase();
    return {
      ...envelope(items.filter((item) => [item.title, item.byline, item.site_name].join(" ").toLowerCase().includes(value))),
      query,
      search_status: "complete",
      search_complete: true,
      search_truncated: false,
      search_truncation_reason: "",
      search_truncation_reasons: [],
      search_scanned_count: items.length,
      search_scan_limit: 2_000,
    };
  },
  async item(id: string): Promise<ReaderItem> {
    const item = items.find((candidate) => candidate.id === id);
    if (!item) throw new Error("item_not_found");
    return { ...item };
  },
  async save(url: string, title = ""): Promise<SaveItemEnvelope> {
    const item = demoItem(`itm_${Date.now()}`, title || new URL(url).hostname, "", new URL(url).hostname, 0, 0, "Preparing this page for reading.");
    items = [item, ...items];
    return { ok: true, item };
  },
  async updateState(id: string, state: Partial<ReaderItem>): Promise<ReaderItem> {
    items = items.map((item) => item.id === id ? { ...item, ...state } : item);
    return { ...(items.find((item) => item.id === id) as ReaderItem) };
  },
  async deleteItem(id: string): Promise<{ ok: boolean; item_id: string; deleted: boolean; cleanup_pending: boolean }> {
    items = items.filter((item) => item.id !== id);
    return { ok: true, item_id: id, deleted: true, cleanup_pending: false };
  },
  async daily(): Promise<DailyEditionEnvelope> {
    return {
      ok: true,
      issue_date: "2026-07-12",
      context_bucket: "morning",
      status: "ready",
      issue: {
        title: "A Quiet Start",
        subtitle: "A quiet set of reads for Sunday morning",
        sections: [{ label: "Now", items: items.map((item, index) => ({ item_id: item.id, rank: index + 1 })) }],
      },
      entries: items.map((item, index) => ({
        item_id: item.id,
        rank: index + 1,
        category_label: "Essay",
        one_line_brief: `A considered read from ${item.site_name}.`,
      })),
    };
  },
  async requestDaily(): Promise<{ ok: boolean; status: string }> {
    return { ok: true, status: "ready" };
  },
  async annotations(itemId: string): Promise<AnnotationEnvelope> {
    return {
      ok: true,
      annotations: itemId === "itm_attention" ? [{ id: "ann_quiet", item_id: itemId, text: "attention is the quietest of our resources", note: "Return to this." }] : [],
      has_more: false,
      next_cursor: null,
    };
  },
  async updateAnnotation(id: string, note: string) {
    return { id, item_id: "itm_attention", text: "attention is the quietest of our resources", note };
  },
  async following(): Promise<FollowingEnvelope> {
    return { ok: true, feed_sources: following.filter((source) => source.enabled) };
  },
  async setFollowing(id: string, enabled: boolean): Promise<FeedSource> {
    following = following.map((source) => source.id === id ? { ...source, enabled } : source);
    const source = following.find((candidate) => candidate.id === id);
    if (!source) throw new Error("feed_source_not_found");
    return source;
  },
  async readingSession(_session: ReadingSessionInput): Promise<{ ok: boolean }> {
    return { ok: true };
  },
  async inboxBriefPreferences(): Promise<InboxBriefPreferences> {
    return { enabled: inboxBriefEnabled, updated_at: "" };
  },
  async updateInboxBriefPreferences(enabled: boolean): Promise<InboxBriefPreferences> {
    inboxBriefEnabled = enabled;
    return { enabled, updated_at: new Date().toISOString() };
  },
  async inboxBriefs(status: "inbox" | "archive"): Promise<InboxBriefEnvelope> {
    return { ok: true, briefs: inboxBrief.status === status ? [{ ...inboxBrief, markdown: undefined, sources: undefined }] : [] };
  },
  async inboxBrief(id: string): Promise<InboxBrief> {
    if (id !== inboxBrief.id) throw new Error("brief_not_found");
    return { ...inboxBrief };
  },
  async requestInboxBrief(): Promise<{ ok: boolean; status: string }> {
    return { ok: true, status: "ready" };
  },
  async keepInboxBriefSource(_briefId: string, itemId: string): Promise<InboxBrief> {
    inboxBrief = { ...inboxBrief, sources: inboxBrief.sources?.map((source) => source.item_id === itemId ? { ...source, disposition: "kept" } : source) };
    return { ...inboxBrief };
  },
  async archiveInboxBrief(): Promise<InboxBrief> {
    inboxBrief = { ...inboxBrief, status: "archive", archived_at: new Date().toISOString(), sources: inboxBrief.sources?.map((source) => source.disposition === "covered" ? { ...source, disposition: "archived" } : source) };
    return { ...inboxBrief };
  },
  async restoreInboxBriefSource(_briefId: string, itemId: string): Promise<InboxBrief> {
    inboxBrief = { ...inboxBrief, sources: inboxBrief.sources?.map((source) => source.item_id === itemId ? { ...source, disposition: "restored" } : source) };
    return { ...inboxBrief };
  },
  async podcastPreferences(): Promise<PodcastPreferencesEnvelope> {
    return {
      ok: true,
      can_manage_feed: true,
      preferences: { ...podcastPreferences },
      feed_url: podcastPreferences.feed_active && podcastFeedUrl ? podcastFeedUrl : undefined,
    };
  },
  async updatePodcastPreferences(enabled: boolean): Promise<PodcastPreferencesEnvelope> {
    podcastPreferences = { enabled, feed_active: enabled, updated_at: new Date().toISOString() };
    podcastFeedUrl = enabled ? "https://imprint.example/podcast/feed/demo-private-token.xml" : "";
    return { ok: true, can_manage_feed: true, preferences: { ...podcastPreferences }, feed_url: podcastFeedUrl || undefined };
  },
  async rotatePodcastFeed(): Promise<PodcastPreferencesEnvelope> {
    podcastPreferences = { ...podcastPreferences, enabled: true, feed_active: true, updated_at: new Date().toISOString() };
    podcastFeedUrl = `https://imprint.example/podcast/feed/demo-private-token-${Date.now()}.xml`;
    return { ok: true, can_manage_feed: true, preferences: { ...podcastPreferences }, feed_url: podcastFeedUrl };
  },
  async revokePodcastFeed(): Promise<PodcastPreferencesEnvelope> {
    podcastPreferences = { ...podcastPreferences, feed_active: false, updated_at: new Date().toISOString() };
    podcastFeedUrl = "";
    return { ok: true, can_manage_feed: true, preferences: { ...podcastPreferences } };
  },
  async podcastEpisodes(status: "inbox" | "archive"): Promise<PodcastEpisodeEnvelope> {
    return { ok: true, episodes: podcastEpisode.status === status ? [{ ...podcastEpisode, sources: undefined }] : [] };
  },
  async podcastEpisode(id: string): Promise<PodcastEpisode> {
    if (id !== podcastEpisode.id) throw new Error("podcast_episode_not_found");
    return { ...podcastEpisode };
  },
  async backfillPodcastArtwork(episodeId: string) {
    return { ok: true, episode_id: episodeId, artwork_jobs_queued: 1 };
  },
  async keepPodcastSource(_episodeId: string, itemId: string): Promise<PodcastEpisode> {
    podcastEpisode = { ...podcastEpisode, sources: podcastEpisode.sources?.map((source) => source.item_id === itemId ? { ...source, state: "kept" } : source) };
    return { ...podcastEpisode };
  },
  async archivePodcastSources(_episodeId: string, itemIds: string[]): Promise<PodcastEpisode> {
    const selected = new Set(itemIds);
    podcastEpisode = { ...podcastEpisode, status: "archive", archived_at: new Date().toISOString(), sources: podcastEpisode.sources?.map((source) => selected.has(source.item_id) && source.state === "pending" ? { ...source, state: "archived" } : source) };
    return { ...podcastEpisode };
  },
  async restorePodcastSource(_episodeId: string, itemId: string): Promise<PodcastEpisode> {
    podcastEpisode = { ...podcastEpisode, sources: podcastEpisode.sources?.map((source) => source.item_id === itemId ? { ...source, state: "restored" } : source) };
    return { ...podcastEpisode };
  },
  async restoreAllPodcastSources(): Promise<PodcastEpisode> {
    podcastEpisode = { ...podcastEpisode, sources: podcastEpisode.sources?.map((source) => source.state === "archived" ? { ...source, state: "restored" } : source) };
    return { ...podcastEpisode };
  },
};

export function demoMode(): boolean {
  if (!import.meta.env.DEV) return false;
  if (new URLSearchParams(window.location.search).has("demo")) sessionStorage.setItem("imprint.demo", "1");
  return sessionStorage.getItem("imprint.demo") === "1";
}
